import { useState } from "react";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import { SUPPORT_EMAIL } from "../config";
import { CheckCircle2, AlertCircle } from "lucide-react";

function validEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s || "");
}

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  // Honeypot: bots will fill this; humans won't see it (CSS-hidden).
  const [company, setCompany] = useState("");
  const [status, setStatus] = useState({ state: "idle", detail: "" });

  const disabled = status.state === "sending" || status.state === "sent";

  async function onSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !validEmail(email) || message.trim().length < 10) {
      setStatus({ state: "error", detail: "Please enter your name, a valid email and at least 10 characters." });
      return;
    }
    setStatus({ state: "sending", detail: "" });
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${base}/api/marketing/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          website: company, // honeypot field name for the server
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `Request failed (${r.status}).`);
      }
      setStatus({ state: "sent", detail: "Thanks — we'll get back to you within one business day." });
      setName("");
      setEmail("");
      setMessage("");
    } catch (err) {
      setStatus({ state: "error", detail: err.message || "Something went wrong. Please email us instead." });
    }
  }

  return (
    <>
      <SEO
        title="Contact"
        description="Reach ClientDesk VA support — every inquiry gets a human reply within one business day."
        path="/contact"
      />
      <section className="pt-20 pb-8 bg-[#FAF7F2]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#E8712C] mb-3">
              Contact
            </div>
            <h1 className="font-display font-extrabold text-[36px] sm:text-[52px] leading-[1.05] tracking-[-0.03em] text-[#1A2A3A] mb-5">
              Talk to a human.
            </h1>
            <p className="text-[17px] text-[#57534E] max-w-xl mx-auto leading-relaxed">
              Questions, feedback, or curious whether ClientDesk VA is right for
              your practice? We reply within one business day.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 bg-white">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          <Reveal>
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-[#FAF7F2] border border-[#E8E2D9] rounded-3xl p-6 sm:p-8"
              noValidate
            >
              <label className="block mb-4">
                <span className="block text-[13px] font-semibold text-[#1A2A3A] mb-1.5">Your name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                  data-testid="contact-name"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E2D9] focus:border-[#E8712C] focus:ring-2 focus:ring-[#E8712C]/20 outline-none text-[15px] text-[#1A2A3A]"
                />
              </label>
              <label className="block mb-4">
                <span className="block text-[13px] font-semibold text-[#1A2A3A] mb-1.5">Email address</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="contact-email"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E2D9] focus:border-[#E8712C] focus:ring-2 focus:ring-[#E8712C]/20 outline-none text-[15px] text-[#1A2A3A]"
                />
              </label>
              <label className="block mb-4">
                <span className="block text-[13px] font-semibold text-[#1A2A3A] mb-1.5">Message</span>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={5}
                  data-testid="contact-message"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E2D9] focus:border-[#E8712C] focus:ring-2 focus:ring-[#E8712C]/20 outline-none text-[15px] text-[#1A2A3A]"
                />
              </label>

              {/* Honeypot — humans don't see this, bots typically fill everything */}
              <label
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-10000px",
                  top: "auto",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                }}
              >
                Website
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </label>

              <button
                type="submit"
                disabled={disabled}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white text-[14px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status.state === "sending" ? "Sending…" : "Send message"}
              </button>

              {status.state === "sent" && (
                <div
                  data-testid="contact-success"
                  className="mt-5 flex items-start gap-2 px-4 py-3 rounded-xl bg-[#E8F0E4] border border-[#B8D5AB] text-[#274F1F] text-[14px]"
                >
                  <CheckCircle2 size={18} className="mt-0.5" />
                  <span>{status.detail}</span>
                </div>
              )}
              {status.state === "error" && (
                <div
                  data-testid="contact-error"
                  className="mt-5 flex items-start gap-2 px-4 py-3 rounded-xl bg-[#FDE9D8] border border-[#F0C69A] text-[#B85C1F] text-[14px]"
                >
                  <AlertCircle size={18} className="mt-0.5" />
                  <span>{status.detail}</span>
                </div>
              )}
            </form>

            <p className="mt-6 text-center text-[13px] text-[#A8A29E]">
              Or email us directly at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#E8712C] font-medium hover:underline">
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
